/**
 * React component that is responsible for rendering a generic CTA-based header,
 *  which includes a title, subtitle, one-two buttons, and a small tagline and footer
 *
 * Copyright (c) 2020 Coursera
 * Author: Kelvin Nguyen
 */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames'
import camelCase from 'lodash/camelCase'
import isString from 'lodash/isString'
import Grid from '@material-ui/core/Grid'
import ModalButton from 'components/ModalButton'
import Button from '../theme/Button'
import Typography from '../theme/Typography'
import styles from './CTAHeader.module.scss'

export const CTAHeaderPropTypes = {
  className: PropTypes.string,
  align: PropTypes.oneOf(['left', 'center', 'right']),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.element]),
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.element]),
  tagline: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.element]),
  footer: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.element]),
  buttons: PropTypes.array,
  titleSettings: PropTypes.object,
  centerDevice: PropTypes.oneOf(['mobile', 'tablet', 'laptop', false]),
}

const CTAHeader = ({
  className,
  align,
  title,
  subtitle,
  tagline,
  footer,
  buttons,
  titleSettings,
  centerDevice,
}) => {
  const alignment = useMemo(() => {
    let alignItems = 'center'
    let justify = 'center'

    switch (align) {
      case 'left':
        alignItems = 'flex-start'
        justify = 'flex-start'
        break
      case 'right':
        alignItems = 'flex-end'
        justify = 'flex-end'
        break
      default:
    }

    return {
      alignItems,
      justify,
    }
  }, [align])

  const Title = useMemo(() => {
    const settings = {
      variant: 'd2',
      ...titleSettings,
      align,
      className: cx('mb-md', titleSettings.className, {
        [`center-${centerDevice}`]: centerDevice,
      }),
    }

    return (
      (title) ? (
        <Typography id={camelCase(title)} {...settings}>
          {title}
        </Typography>
      ) : null
    )
  }, [title, align, titleSettings, centerDevice])

  const Subtitle = useMemo(() => {
    const variant = (!title) ? 'h2' : 'body1'

    return (isString(subtitle)) ? (
      <Typography variant={variant} align={align} fontWeight={400}>
        {subtitle}
      </Typography>
    ) : (
      <div className={cx(`text-${align}`, 'heroSubtitle', styles.subtitle)}>
        {subtitle}
      </div>
    )
  }, [title, subtitle, align])

  const Tagline = useMemo(() => (
    (tagline) ? (
      <Typography className={cx('mb-lg')} variant="h3" fontWeight={700} align={align}>
        {tagline}
      </Typography>
    ) : null
  ), [tagline, align])

  const Footer = useMemo(() => (
    (footer) ? (
      <Typography variant="h3" fontWeight={400} align={align}>
        {footer}
      </Typography>
    ) : null
  ), [footer, align])

  const Buttons = useMemo(() => {
    const CTAButtons = buttons.map(({
      id,
      variant,
      text,
      href,
      isDarkBackground,
      modalContent,
      className: btnClass,
    }) => {
      let button = null
      if (modalContent) {
        button = (
          <ModalButton
            key={`ctaButton-${id}`}
            variant={variant}
            className={cx(btnClass, 'cta-link')}
            isDarkBackground={isDarkBackground}
            text={text}>
            {modalContent}
          </ModalButton>
        )
      } else if (href) {
        button = (
          <a key={`ctaButton-${id}`} href={href} className={cx(btnClass, 'cta-link')}>
            <Button variant={variant}>
              {text}
            </Button>
          </a>
        )
      }

      return button
    })

    const btnClass = cx(styles.ctaButtonsContainer, 'cta-buttons-container', {
      'mt-md': subtitle || title,
      'mb-md': !!footer,
      [`center-${centerDevice}`]: centerDevice,
    })

    return (CTAButtons.length > 0) ? (
      <Grid
        className={btnClass}
        direction="row"
        container
        {...alignment}>
        {CTAButtons}
      </Grid>
    ) : null
  }, [title, subtitle, buttons, footer, alignment, centerDevice])

  return (
    <Grid
      className={cx(className, {
        [`center-${centerDevice}`]: centerDevice,
      })}
      direction="column"
      container
      item
      {...alignment}>
      {Tagline}
      {Title}
      {Subtitle}
      {Buttons}
      {Footer}
    </Grid>
  )
}

CTAHeader.defaultProps = {
  align: 'center',
  buttons: [],
  titleSettings: {},
  centerDevice: false,
}

CTAHeader.propTypes = CTAHeaderPropTypes

export default CTAHeader
